<template>
  <!-- BUTTON -->
  <button
    v-if="props.variant !== ButtonVariant.Link"
    :id="props.id"
    :class="definedClasses"
    :disabled="props.isDisabled"
    :name="props.name">
    <component
      :is="props.iconLeft"
      v-if="props.iconLeft" />
    <span v-if="props.label">{{ label }}</span>
    <span v-else-if="hasDefaultSlot"><slot /></span>
    <component
      :is="props.iconRight"
      v-if="props.iconRight" />
  </button>

  <!-- LINK -->
  <a
    v-else
    :id="props.id"
    :href="sanitizeURL(props.to)"
    :target="props.target"
    :download="props.download"
    :class="definedClasses"
    >{{ props.label }}</a
  >
</template>

<script setup lang="ts">
  import DOMPurify from 'dompurify';
  import { computed, ComputedRef, useSlots } from 'vue';

  const purify = DOMPurify(window);
  const slots = useSlots();
  const hasDefaultSlot = slots.default?.();

  // Types

  enum ButtonVariant {
    Primary = 'primary',
    Secondary = 'secondary',
    Tertiary = 'tertiary',
    Success = 'success',
    NoVariant = 'noVariant',
    Link = 'link',
    SecondaryColorHollow = 'secondaryColorHollow',
  }
  type TVariant = `${ButtonVariant}`;
  type TClassName = string | Record<string, boolean> | null;
  type TAnchor = '_blank' | '_self' | '_parent' | '_top';

  interface IButtonProps {
    id?: string;
    label?: string;
    className?: TClassName;
    variant?: TVariant;
    rounded?: boolean;
    isDisabled?: boolean;
    iconLeft?: any;
    iconRight?: any;
    to?: never;
    target?: never;
    download?: never;
    name?: string;
    borderless?: boolean;
  }

  type TKeyLinkProps = 'id' | 'label' | 'className' | 'variant' | 'to' | 'target' | 'download';
  type TButtonPropsUnavailable = Omit<Partial<Record<keyof IButtonProps, never>>, TKeyLinkProps>;

  interface ILinkProps extends /** @vue-ignore */ TButtonPropsUnavailable {
    id?: string;
    label: string;
    className?: TClassName;
    variant: `${ButtonVariant.Link}`;
    to?: string;
    target?: TAnchor;
    download?: string;
  }

  // Props
  const props = withDefaults(defineProps<IButtonProps | ILinkProps>(), {
    isDisabled: false,
    variant: ButtonVariant.Primary,
    borderless: false,
  });

  // Classes
  const btnClass = 'btn';
  const roundedClass = 'btn-rounded';
  const iconOnlyClass = 'icon-only';
  const variantClasses: Record<TVariant, string> = {
    primary: 'btn-primary',
    secondary: 'btn-primary-hollow',
    tertiary: 'btn-default',
    success: 'btn-success',
    noVariant: 'btn-no-variant',
    link: 'btn-link',
    secondaryColorHollow: 'btn-secondary-hollow',
  };

  // Computed
  const hasIconOnly: ComputedRef<boolean> = computed(() => props.label == null && hasDefaultSlot == null);

  const definedClasses: ComputedRef<TClassName[]> = computed(() => [
    [ButtonVariant.NoVariant, ButtonVariant.Link].includes(props.variant as ButtonVariant) ? null : btnClass,
    variantClasses[props.variant],
    props.rounded ? roundedClass : null,
    props.className ?? null,
    hasIconOnly.value ? iconOnlyClass : null,
    props.borderless ? 'borderless' : null,
  ]);

  // Methods
  function sanitizeURL(to: string | undefined) {
    if (!to) return undefined;
    return purify.sanitize(to);
  }
</script>

<style lang="scss" scoped>
  #app {
    .btn-rounded {
      border-radius: 2rem;
    }
    .borderless {
      border: none;
      box-shadow: none;
    }
    .btn-no-variant {
      font-size: 1.4rem;
      padding: 1.1rem;
      cursor: pointer;
      display: flex;
      align-items: center;
      background: transparent;
      border: none;
      outline: none;

      :deep(svg) {
        width: 1.6rem;
        position: relative;
        stroke: var(--defaultButtonColor);
      }

      &:hover {
        :deep(svg) {
          stroke: var(--primaryColorLight);
        }
      }
    }

    .btn.icon-only {
      padding: 1rem;
      :deep(svg) {
        margin-right: 0rem;
      }
    }

    .btn-link {
      font-size: 1.4rem;
      text-transform: none;
    }
  }
</style>
